import React, { useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Download, Upload, FileSpreadsheet, Check, Loader2, AlertTriangle, X } from 'lucide-react';
import { useT } from '../../utils/translate';
import { useModalOpen } from '../../utils/modalLock';
import { EDU, apiErr, downloadFile } from './shared';

// Импорт учеников из Excel: 1) скачать шаблон, 2) выбрать заполненный файл, 3) «Проверить» (ничего не сохраняется), 4) «Загрузить».
export default function ImportStudentsModal({ defaultGroup, onClose, onDone }) {
  const t = useT(); useModalOpen();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState('');           // '' | tpl | check | import
  const [check, setCheck] = useState(null);       // результат проверки
  const [result, setResult] = useState(null);     // результат загрузки
  const fileRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { toast.error(t('edu.io.onlyXlsx')); return; }
    setFile(f); setCheck(null); setResult(null);
  };
  const run = async (dry) => {
    if (!file) return;
    setBusy(dry ? 'check' : 'import');
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${EDU}/students/import`, fd, { params: { ...(dry ? { dryRun: 1 } : {}), ...(defaultGroup ? { group: defaultGroup } : {}) }, headers: { 'Content-Type': 'multipart/form-data' } });
      if (dry) setCheck(r.data); else { setResult(r.data); onDone?.(); }
    } catch (e) { apiErr(e, t('edu.io.importError')); } finally { setBusy(''); }
  };
  const tpl = async () => {
    setBusy('tpl');
    try { await downloadFile(`${EDU}/students/template`, {}, 'template.xlsx'); } catch (e) { apiErr(e, t('edu.io.templateError')); } finally { setBusy(''); }
  };

  const shown = result || check;
  const errText = (e) => `${t(`edu.io.err.${e.code}`) !== `edu.io.err.${e.code}` ? t(`edu.io.err.${e.code}`) : t('edu.io.err.failed')}${e.detail ? `: ${e.detail}` : ''}`;
  const errBlock = (list) => list.length > 0 && (
    <div className="mt-3">
      <p className="text-sm font-medium text-ink flex items-center gap-1.5 mb-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /> {t('edu.io.problems', { n: list.length })}</p>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-surface-200 divide-y divide-surface-100 text-sm">
        {list.map((e, i) => (
          <div key={i} className="flex gap-3 px-3 py-2">
            <span className="text-ink-tertiary tabular-nums shrink-0 whitespace-nowrap min-w-[3.5rem]">{t('edu.io.row', { n: e.row })}</span>
            <span className="min-w-0"><span className="font-medium text-ink">{e.name || '—'}</span><span className="block text-xs text-red-700">{errText(e)}</span></span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-lg flex flex-col max-h-[88dvh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <h2 className="font-semibold text-ink flex items-center gap-2"><Upload className="w-4 h-4 text-primary-600" /> {t('edu.io.title')}</h2>
          <button type="button" onClick={onClose} aria-label={t('edu.io.close')} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {result ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3"><Check className="w-7 h-7 text-emerald-700" /></div>
              <p className="text-lg font-bold text-ink">{t('edu.io.doneTitle')}</p>
              <p className="text-sm text-ink-secondary mt-1"><b className="text-emerald-700">{result.created}</b> {t('edu.io.created')}{result.skipped > 0 && <>, <b>{result.skipped}</b> {t('edu.io.skipped')}</>}</p>
              <div className="text-left">{errBlock(result.errors || [])}</div>
            </div>
          ) : (
            <>
              <section>
                <p className="text-sm font-semibold text-ink">1. {t('edu.io.step1')}</p>
                <p className="text-xs text-ink-tertiary mt-0.5 mb-2">{t('edu.io.step1Hint')}</p>
                <button type="button" className="btn-secondary btn-md" onClick={tpl} disabled={busy === 'tpl'}>
                  {busy === 'tpl' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t('edu.io.template')}
                </button>
              </section>

              <section>
                <p className="text-sm font-semibold text-ink">2. {t('edu.io.step2')}</p>
                <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
                  className="mt-2 border-2 border-dashed border-surface-200 rounded-xl px-4 py-6 text-center hover:border-primary-300 transition-colors">
                  <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
                  {file ? (
                    <p className="text-sm text-ink flex items-center justify-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-700" /> {file.name}</p>
                  ) : (
                    <p className="text-sm text-ink-tertiary">{t('edu.io.drop')}</p>
                  )}
                  <button type="button" className="btn-secondary btn-sm mt-3" onClick={() => fileRef.current?.click()}>{file ? t('edu.io.change') : t('edu.io.pick')}</button>
                </div>
              </section>

              {check && (
                <section>
                  <div className={`rounded-xl px-3 py-2.5 text-sm ${check.created > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                    {check.total === 0 ? t('edu.io.empty') : t('edu.io.checked', { ok: check.created, bad: check.skipped })}
                  </div>
                  {errBlock(check.errors || [])}
                </section>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-surface-100 shrink-0 flex flex-wrap justify-end gap-2">
          {result ? (
            <button type="button" className="btn-primary btn-md" onClick={onClose}>{t('edu.io.close')}</button>
          ) : (
            <>
              <button type="button" className="btn-secondary btn-md" onClick={onClose}>{t('edu.io.cancel')}</button>
              <button type="button" className="btn-secondary btn-md" disabled={!file || !!busy} onClick={() => run(true)}>
                {busy === 'check' && <Loader2 className="w-4 h-4 animate-spin" />} {t('edu.io.check')}
              </button>
              <button type="button" className="btn-primary btn-md" disabled={!check || check.created === 0 || !!busy} onClick={() => run(false)}>
                {busy === 'import' && <Loader2 className="w-4 h-4 animate-spin" />} {check ? t('edu.io.importN', { n: check.created }) : t('edu.io.import')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
